import { Box, FormField, InputArea, Text, TextButton } from '@wix/design-system';
import { SparklesSmall } from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { RicosContext } from './../../../ricos-context';
import type { PluginExports } from './../../../ricos-types';

import {
  AI_EXPORTS_ALT_TEXT_GENERATE_BUTTON,
  AI_EXPORTS_ALT_TEXT_INPUT_AREA,
} from '../../data-hooks';
import type { AiPluginEditorConfig } from '../../types';
import { getImageUrl } from '../../utils/get-image-url';
import { useAiRequestService } from '../../utils/use-ai-request-service';

type IntegrationProps = React.ComponentProps<
  Required<PluginExports['aiIntegrations']['image']>['altTextInput']
>;

type AltTextInputProps = IntegrationProps & {
  config: AiPluginEditorConfig;
};

type RequestState =
  | {
      type: 'INIT';
    }
  | {
      type: 'LOADING';
    }
  | {
      type: 'ERROR';
      error: string;
    };

const AltTextInput: React.FC<AltTextInputProps> = ({
  image,
  altText,
  onChange,
  config,
  tooltipAppendTo,
  logBi,
  disabled,
}) => {
  const { t, localeContent } = useContext(RicosContext);
  const { fetchPrompt } = useAiRequestService(config);
  const [requestState, setRequestState] = React.useState<RequestState>({ type: 'INIT' });
  const inputRef = React.useRef<InputArea>(null);
  const isDestroyed = React.useRef(false);

  React.useEffect(() => {
    isDestroyed.current = false;
    return () => {
      isDestroyed.current = true;
    };
  }, []);

  const generateAltText = async () => {
    if (!image) {
      return;
    }

    setRequestState({ type: 'LOADING' });
    inputRef.current?.calculateComputedRows();
    const result = await fetchPrompt({
      promptSlug: 'generate-alt-text',
      promptParams: {
        imageUrl: getImageUrl({ image }),
        siteLanguage: localeContent,
      },
      additionalTags: 'image alt text generation',
    });

    if (isDestroyed.current) {
      return;
    }

    if (result.type === 'SUCCESS') {
      setRequestState({ type: 'INIT' });
      logBi({
        dataHook: AI_EXPORTS_ALT_TEXT_GENERATE_BUTTON,
        value: result.results?.[0] ?? altText,
      });
      onChange(result.results?.[0] ?? altText);
      inputRef.current?.calculateComputedRows();
      return;
    }

    setRequestState({ type: 'ERROR', error: t('AIPlugin_Prompt_GenerateAltText_Error') });
  };

  return (
    <FormField
      label={t('ImageSettings_Alt_Label')}
      infoContent={
        <Box direction="vertical" gap="SP1">
          <Text light size="small">
            {t('ImageSettings_Alt_Label_Tooltip')}
          </Text>
        </Box>
      }
      infoTooltipProps={{
        appendTo:
          tooltipAppendTo ??
          (el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal'))),
      }}
      suffix={
        image ? (
          <Box direction="vertical" gap="SP1">
            <TextButton
              dataHook={AI_EXPORTS_ALT_TEXT_GENERATE_BUTTON}
              disabled={requestState.type === 'LOADING' || disabled}
              size="tiny"
              prefixIcon={<SparklesSmall />}
              onClick={() => {
                logBi({ dataHook: AI_EXPORTS_ALT_TEXT_GENERATE_BUTTON });
                generateAltText();
              }}
            >
              {t('AIPlugin_ImagePlugin_AltText_Generate_Text')}
            </TextButton>
          </Box>
        ) : undefined
      }
    >
      <InputArea
        ref={inputRef}
        dataHook={AI_EXPORTS_ALT_TEXT_INPUT_AREA}
        placeholder={t('ImageSettings_Alt_Input_Placeholder')}
        onChange={e => {
          logBi({ dataHook: AI_EXPORTS_ALT_TEXT_INPUT_AREA, value: e.target.value });
          onChange(e.target.value);
        }}
        value={
          requestState.type === 'LOADING' ? t('AIPlugin_ImagePlugin_AltText_InProgress') : altText
        }
        rows={3}
        readOnly={requestState.type === 'LOADING'}
        status={
          requestState.type === 'ERROR'
            ? 'error'
            : requestState.type === 'LOADING'
            ? 'loading'
            : undefined
        }
        statusMessage={requestState.type === 'ERROR' ? requestState.error : undefined}
        tooltipPlacement="top-end"
        size="small"
        disabled={disabled}
      />
    </FormField>
  );
};

export const createAltTextInput = (config: AiPluginEditorConfig) => {
  return (props: IntegrationProps) => <AltTextInput {...props} config={config} />;
};
